// messageCenter/attachmentPicker.js — files for a ticket or a comment: paste a screenshot (Ctrl+V), drop files or pick
// them; images are shrunk to the server's limit (attachmentShrink.js), every file gets a role (before / after / info)
// and can be removed again. Stateful component: { element, attachTo, getFiles, whenReady, clear, destroy }.
//
// `limits` come from the page ({ fileBytes, requestBytes, files }, MessageCenterUpload::serverLimits): the browser
// checks against them before sending, so an upload is not cut off silently by PHP.
import { str } from '../i18n/str.js';
import { createDisposers } from '../core/disposers.js';
import { ACCEPTED_TYPES, shrinkImage } from './attachmentShrink.js';

export const ROLE_LABEL_KEYS = Object.freeze({ before: 'MC_ROLE_BEFORE', after: 'MC_ROLE_AFTER', info: 'MC_ROLE_INFO' });
/** Used when the page sends no limits: the smallest PHP defaults (2 MB per file, 8 MB per request). */
export const FALLBACK_LIMITS = Object.freeze({ fileBytes: 2 * 1024 * 1024, requestBytes: 8 * 1024 * 1024, files: 10 });
const REQUEST_SAFETY = 0.9;   // leave room for the text fields of the request

export function createAttachmentPicker({ limits, shrink = shrinkImage }) {
    const disposers = createDisposers();
    const items = [];   // { id, file, role, url }
    let nextId = 1;
    let pending = Promise.resolve();

    const element = document.createElement('div');
    element.className = 'attachmentPicker';

    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = ACCEPTED_TYPES.join(',');
    input.hidden = true;
    const pick = document.createElement('button');
    pick.type = 'button';
    pick.className = 'topo2-btn topo2-btn--small attachmentPicker__pick';
    pick.textContent = str('MC_ATTACH_PICK');
    const hint = document.createElement('span');
    hint.className = 'attachmentPicker__hint';
    hint.textContent = str('MC_ATTACH_HINT');
    const message = document.createElement('p');
    message.className = 'attachmentPicker__message';
    message.hidden = true;
    const list = document.createElement('ul');
    list.className = 'attachmentPicker__list';
    element.append(input, pick, hint, message, list);

    listen(pick, 'click', () => input.click());
    listen(input, 'change', () => { add([...input.files]); input.value = ''; });
    attachTo(element);

    function listen(target, event, fn) {
        target.addEventListener(event, fn);
        disposers.add(() => target.removeEventListener(event, fn));
    }

    /** Also take pasted and dropped files on another element (e.g. the text editor or the whole form). */
    function attachTo(target) {
        // defaultPrevented: the picker sits inside a target it is attached to, so an event reaches two listeners
        listen(target, 'paste', (event) => {
            const files = [...(event.clipboardData?.files ?? [])];
            if (event.defaultPrevented || !files.length) { return; }   // a plain text paste stays with the text field
            event.preventDefault();
            add(files);
        });
        listen(target, 'dragover', (event) => {
            if (!event.dataTransfer?.types?.includes('Files')) { return; }
            event.preventDefault();
            element.classList.add('is-dragover');
        });
        listen(target, 'dragleave', () => element.classList.remove('is-dragover'));
        listen(target, 'drop', (event) => {
            const files = [...(event.dataTransfer?.files ?? [])];
            element.classList.remove('is-dragover');
            if (event.defaultPrevented || !files.length) { return; }
            event.preventDefault();
            add(files);
        });
    }

    /** Checks, shrinks and lists the files one after another; whenReady() waits for it. */
    function add(files) {
        showMessage([]);
        const problems = [];
        pending = pending.then(async () => {
            element.classList.add('is-busy');
            for (const original of files) {
                if (!ACCEPTED_TYPES.includes(original.type)) { problems.push(text('MC_ATTACH_TYPE', original.name)); continue; }
                if (items.length >= limits.files) { problems.push(text('MC_ATTACH_TOO_MANY', String(limits.files))); break; }
                let file;
                try {
                    file = await shrink(original, limits.fileBytes);
                } catch (e) {
                    console.error('message center: shrinking failed', e);
                    problems.push(text('MC_ATTACH_TOO_BIG', original.name));
                    continue;
                }
                if (file.size > limits.fileBytes) { problems.push(text('MC_ATTACH_TOO_BIG', original.name)); continue; }
                if (totalBytes() + file.size > limits.requestBytes * REQUEST_SAFETY) { problems.push(text('MC_ATTACH_REQUEST_TOO_BIG', original.name)); continue; }
                const item = { id: nextId++, file, role: 'info', url: previewUrl(file) };
                items.push(item);
                list.append(row(item));
            }
            element.classList.remove('is-busy');
            showMessage(problems);
        });
        return pending;
    }

    function row(item) {
        const li = document.createElement('li');
        li.className = 'attachmentPicker__item';
        li.dataset.id = item.id;
        if (item.url) {
            const img = document.createElement('img');
            img.className = 'attachmentPicker__thumb';
            img.src = item.url;
            img.alt = '';
            li.append(img);
        } else {
            const badge = document.createElement('span');
            badge.className = 'attachmentPicker__badge';
            badge.textContent = 'PDF';
            li.append(badge);
        }
        const name = document.createElement('span');
        name.className = 'attachmentPicker__name';
        name.textContent = item.file.name + ' (' + Math.ceil(item.file.size / 1024) + ' KB)';
        const role = document.createElement('select');
        for (const [value, key] of Object.entries(ROLE_LABEL_KEYS)) {
            const o = document.createElement('option');
            o.value = value;
            o.textContent = str(key);
            role.append(o);
        }
        role.value = item.role;
        role.addEventListener('change', () => { item.role = role.value; });
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'topo2-btn topo2-btn--small attachmentPicker__remove';
        remove.textContent = str('REMOVE');
        remove.addEventListener('click', () => {
            items.splice(items.indexOf(item), 1);
            if (item.url) { URL.revokeObjectURL(item.url); }
            li.remove();
        });
        li.append(name, role, remove);
        return li;
    }

    function previewUrl(file) {
        return file.type.startsWith('image/') && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '';
    }

    function totalBytes() {
        return items.reduce((sum, item) => sum + item.file.size, 0);
    }

    function text(key, name) {
        return str(key).replace('{{name}}', () => name);
    }

    function showMessage(problems) {
        message.textContent = problems.join(' · ');
        message.hidden = problems.length === 0;
    }

    /** [{ file, role }] in the order they were added. */
    function getFiles() {
        return items.map(({ file, role }) => ({ file, role }));
    }

    /** Resolves when every added file is checked and shrunk. */
    function whenReady() {
        return pending;
    }

    function clear() {
        for (const item of items) { if (item.url) { URL.revokeObjectURL(item.url); } }
        items.length = 0;
        list.replaceChildren();
        showMessage([]);
    }

    function destroy() {
        clear();
        disposers.dispose();
        element.remove();
    }

    return { element, attachTo, add, getFiles, whenReady, clear, destroy };
}
